import { useState, useMemo, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { nameMap } from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import Styles from './WhiteLabelCheckoutModal.module.scss'

interface WhiteLabelCheckoutModalProps {
  onConfirm: (priceId: string, paymentMethodId?: string) => void
  onCancel?: () => void
}

export const WhiteLabelCheckoutModal = observer(({ onConfirm, onCancel }: WhiteLabelCheckoutModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const { data: currentPlan } = useCurrentPlan()
  const { autopilotAddOns } = useRootStore().tierUsageStore
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const stripeFormRef = useRef<StripePaymentFormRef>(null)

  const pricing = useMemo(() => {
    const currentPrice = currentPlan?.prices?.[0]
    const intervalCount = currentPrice?.recurring?.interval_count || 1
    const whiteLabelAddon = autopilotAddOns.find((addon) => addon.key === PaymentPlanKey.BlazeAutopilotWhiteLabel)
    const whiteLabelPrice = whiteLabelAddon?.prices.find((p) => p.recurring?.interval_count === intervalCount)

    const currentMonthly = currentPrice ? Math.round(currentPrice.unit_amount / 100 / intervalCount) : 0
    const addonMonthly = whiteLabelPrice ? Math.round(whiteLabelPrice.unit_amount / 100 / intervalCount) : 0

    const planName = currentPlan?.plan ? nameMap[currentPlan.plan] ?? 'Plan' : 'Plan'
    const billingPeriod = intervalCount === 1 ? 'Monthly' : `${intervalCount} months`
    const nextRenewalDate = currentPlan?.next_payment?.due_date
      ? new Date(currentPlan.next_payment.due_date * 1000).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
      : null

    return {
      priceId: whiteLabelPrice?.id || null,
      currentMonthly,
      addonMonthly,
      newTotal: currentMonthly + addonMonthly,
      planName,
      billingPeriod,
      nextRenewalDate,
    }
  }, [autopilotAddOns, currentPlan])

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!pricing.priceId) return
    setIsProcessing(true)
    try {
      await onConfirm(pricing.priceId, paymentMethodId)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleUpdate = async () => {
    if (stripeFormRef.current) {
      await stripeFormRef.current.submitPayment()
    }
  }

  const canPurchase = !!pricing.priceId && !!clientSecret && !isLoading

  return (
    <Modal.Root size="sm" bodyClassName={Styles.modal}>
      <Modal.Content>
        <div className={Styles.header}>
          <Heading level={3}>Add white label to your plan</Heading>
        </div>

        <div className={Styles.content}>
          <div className={Styles.pricingSection}>
            <div className={Styles.currentPlanRow}>
              <div className={Styles.planLabel}>
                <span className={Styles.planName}>
                  You're on {pricing.planName} Plan / {pricing.billingPeriod}
                </span>
              </div>
              <div className={Styles.price}>${pricing.currentMonthly}/mo</div>
            </div>

            <div className={Styles.addonRow}>
              <div className={Styles.addonLabel}>Add-on white label</div>
              <div className={Styles.price}>+ ${pricing.addonMonthly}/mo</div>
            </div>

            <div className={Styles.totalRow}>
              <div className={Styles.totalLabel}>New monthly total</div>
              <div className={Styles.totalPrice}>${pricing.newTotal}/mo</div>
            </div>
          </div>

          <div className={Styles.prorationNotice}>
            <Text variant="secondary" color="var(--dark-60)">
              Your first charge will be prorated for the rest of this billing cycle.
              {pricing.nextRenewalDate &&
                ` Future months will renew at $${pricing.newTotal}/mo starting ${pricing.nextRenewalDate}.`}
            </Text>
          </div>

          {!pricing.priceId && (
            <div className={Styles.errorNotice}>
              <Text variant="secondary" color="var(--red-50)">
                Unable to load pricing. Please refresh and try again.
              </Text>
            </div>
          )}

          {clientSecret && pricing.priceId && (
            <StripePaymentForm
              ref={stripeFormRef}
              clientSecret={clientSecret}
              onPaymentMethodCreated={handlePaymentMethodCreated}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
            />
          )}
        </div>

        <div className={Styles.footer}>
          <Button variant="secondary" size="lg" onPress={onCancel} isDisabled={isProcessing}>
            Cancel
          </Button>
          <Button variant="primary" size="lg" onPress={handleUpdate} isDisabled={isProcessing || !canPurchase}>
            {isProcessing ? 'Processing...' : 'Update'}
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
